import * as React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  Grid,
  Box,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { useForm, Controller } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useMutation, useQueryClient } from 'react-query';
import {
  PodTimetablesReadDTO,
  PodTimetablesCreateDTO,
  IPodTimetablesCreateDTO,
} from '../../../generated/api-client';
import { apiClient } from '../../../services/apiClient';
import { ActionButton, UnitOfMeasurementDropdown } from '../../common';
import { EnergyUseDropdown } from '../../common/EnergyUseDropdown';
import { FUNCTIONAL_AREA_OPTIONS } from '../../../utils/functionalArea';

/** Unità di misura di default per i POD orari (solo Energia Elettrica). */
const DEFAULT_POD_UNIT = 'kWh';

interface EditPodTimetableDialogProps {
  open: boolean;
  onClose: () => void;
  podTimetable: PodTimetablesReadDTO | null;
  diagnosisId: string;
  existingPodTimetables?: PodTimetablesReadDTO[];
}

export const EditPodTimetableDialog: React.FC<EditPodTimetableDialogProps> = ({
  open,
  onClose,
  podTimetable,
  diagnosisId,
  existingPodTimetables = [],
}) => {
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isDirty },
  } = useForm<PodTimetablesCreateDTO & { unit_of_measurement?: string }>({
    defaultValues: podTimetable
      ? {
          diagnosis_id: diagnosisId,
          code: podTimetable.code ?? undefined,
          unit_of_measurement: podTimetable.unit_of_measurement ?? undefined,
          hierarchy: podTimetable.hierarchy ?? undefined,
          functional_area: podTimetable.functional_area ?? undefined,
          energy_use_id: (podTimetable as any).energy_use_id ?? undefined,
          data_grouping: podTimetable.data_grouping ?? undefined,
          chart: podTimetable.chart ?? undefined,
          notes: podTimetable.notes ?? undefined,
          total_consumption_verification: podTimetable.total_consumption_verification ?? false,
        }
      : undefined,
  });

  // Usa un ref per tracciare se il dialog è stato appena aperto
  const isInitialMount = React.useRef(true);
  const previousOpen = React.useRef(open);

  React.useEffect(() => {
    // Reset solo quando il dialog viene aperto (non quando cambia defaultUnitOfMeasurement)
    if (open && (isInitialMount.current || previousOpen.current !== open)) {
      if (podTimetable) {
        reset({
          diagnosis_id: diagnosisId,
          code: podTimetable.code ?? undefined,
          unit_of_measurement: podTimetable.unit_of_measurement ?? undefined,
          hierarchy: podTimetable.hierarchy ?? undefined,
          functional_area: podTimetable.functional_area ?? undefined,
          energy_use_id: (podTimetable as any).energy_use_id ?? undefined,
          data_grouping: podTimetable.data_grouping ?? undefined,
          chart: podTimetable.chart ?? undefined,
          notes: podTimetable.notes ?? undefined,
          total_consumption_verification: podTimetable.total_consumption_verification ?? false,
        });
      } else {
        reset({
          diagnosis_id: diagnosisId,
          code: undefined,
          unit_of_measurement: undefined,
          hierarchy: undefined,
          functional_area: undefined,
          energy_use_id: undefined,
          data_grouping: undefined,
          chart: undefined,
          notes: undefined,
          total_consumption_verification: false,
        });
      }
      isInitialMount.current = false;
    }
    previousOpen.current = open;
  }, [podTimetable, diagnosisId, reset, open]);

  const createMutation = useMutation(
    async (data: PodTimetablesCreateDTO) => {
      return await apiClient.create_item_pod_timetables__post(data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`pod-timetables-${diagnosisId}`);
        toast.success('POD orario creato con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || 'Errore nella creazione del POD orario');
      },
    }
  );

  const updateMutation = useMutation(
    async (data: PodTimetablesCreateDTO) => {
      if (!podTimetable?.id) {
        throw new Error('ID POD orario mancante');
      }
      return await apiClient.update_item_pod_timetables__item_id__put(podTimetable.id, data);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(`pod-timetables-${diagnosisId}`);
        toast.success('POD orario aggiornato con successo');
        reset();
        onClose();
      },
      onError: (error: any) => {
        toast.error(error?.message || "Errore nell'aggiornamento del POD orario");
      },
    }
  );

  const onSubmit = (data: PodTimetablesCreateDTO & { unit_of_measurement?: string }) => {
    const submitData: IPodTimetablesCreateDTO = {
      ...data,
      unit_of_measurement: data.unit_of_measurement ?? DEFAULT_POD_UNIT,
    };

    if (podTimetable) {
      updateMutation.mutate(submitData as PodTimetablesCreateDTO);
    } else {
      // Calculate index for new POD (set to last position)
      const maxIndex = existingPodTimetables.reduce((max, pod) => Math.max(max, pod.index ?? 0), 0);
      submitData.index = maxIndex + 1;
      createMutation.mutate(submitData as PodTimetablesCreateDTO);
    }
  };

  const handleClose = () => {
    if (!updateMutation.isLoading && !createMutation.isLoading) {
      reset();
      onClose();
    }
  };

  const isSubmitting = updateMutation.isLoading || createMutation.isLoading;

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{podTimetable ? 'Modifica POD orario' : 'Nuovo POD orario'}</span>
          <ActionButton
            icon={<CloseIcon />}
            tooltip="Chiudi"
            onClick={handleClose}
            variant="outlined"
          />
        </Box>
      </DialogTitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Codice"
                {...register('code')}
                error={!!errors.code}
                helperText={errors.code?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="unit_of_measurement"
                control={control}
                render={({ field, fieldState }) => (
                  <UnitOfMeasurementDropdown
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    defaultUnitOfMeasurement={DEFAULT_POD_UNIT}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Gerarchia"
                {...register('hierarchy')}
                error={!!errors.hierarchy}
                helperText={errors.hierarchy?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth error={!!errors.functional_area}>
                <InputLabel>Area funzionale</InputLabel>
                <Controller
                  name="functional_area"
                  control={control}
                  render={({ field }) => (
                    <Select {...field} label="Area funzionale">
                      <MenuItem value="">
                        <em>Nessuna</em>
                      </MenuItem>
                      {FUNCTIONAL_AREA_OPTIONS.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                {errors.functional_area && (
                  <FormHelperText>{errors.functional_area.message}</FormHelperText>
                )}
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Controller
                name="energy_use_id"
                control={control}
                render={({ field, fieldState }) => (
                  <EnergyUseDropdown
                    diagnosisId={diagnosisId}
                    value={field.value ?? null}
                    onChange={field.onChange}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Raggruppamento dato"
                {...register('data_grouping')}
                error={!!errors.data_grouping}
                helperText={errors.data_grouping?.message}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                label="Grafico"
                {...register('chart')}
                error={!!errors.chart}
                helperText={errors.chart?.message}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Note"
                {...register('notes')}
                error={!!errors.notes}
                helperText={errors.notes?.message}
                multiline
                rows={3}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Controller
                name="total_consumption_verification"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={field.value ?? false}
                        onChange={e => field.onChange(e.target.checked)}
                      />
                    }
                    label="Verifica totale consumi"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Stack direction="row" justifyContent="center" sx={{ width: '100%', py: 1 }}>
            <ActionButton
              icon={<SaveIcon />}
              tooltip={'Salva'}
              loading={isSubmitting}
              disabled={isSubmitting || !isDirty}
              buttonType="submit"
            />
          </Stack>
        </DialogActions>
      </form>
    </Dialog>
  );
};

